import { PlatformEarningsDetails } from '@/components/reports/PlatformEarningsBreakdown'
import { InfoCard } from '@/components/contracts/InfoCard'
import ReportDateRangeFilterBar from '@/components/reports/ReportDateRangeFilterBar'
import { InfoNotice } from '@/components/patterns/info-notice'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import OrderSourceBadge from '@/components/orders/OrderSourceBadge'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePathHelper, type PathHelpers, type PathLink } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import { REPORT_DATE_MODE, getOrganizationReportDateBounds } from '@/lib/report-date-range'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  PLATFORM_LEDGER_ACCOUNT,
  PLATFORM_LEDGER_ACCOUNT_OPTIONS,
  PLATFORM_LEDGER_BASIS,
  PLATFORM_LEDGER_SORT,
  PLATFORM_LEDGER_VIEW,
  PLATFORM_LEDGER_VIEW_OPTIONS,
  getDefaultPlatformLedgerReportFilters,
  getPlatformLedgerReportFiltersFromSearch,
  getPlatformLedgerReportSearchParams,
  platformLedgerReportSearchSchema,
  type PlatformLedgerReportFilters,
} from '@/lib/services/platform-ledger/platform-ledger.schema'
import type {
  PlatformLedgerAccount,
  PlatformLedgerBasis,
  PlatformLedgerEntry,
  PlatformLedgerEntryMetadata,
  PlatformLedgerView,
} from '@/lib/services/platform-ledger/platform-ledger.types'
import {
  platformLedgerQueries,
  useExportPlatformLedgerEntriesMutation,
  usePlatformLedgerEntries,
} from '@/lib/services/platform-ledger/platform-ledger.query'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import { cn, formatCurrency, parseCurrency, unslugify } from '@/lib/utils'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import type { Dayjs } from 'dayjs'
import {
  Banknote,
  BadgeDollarSign,
  BookOpenCheck,
  Clock3,
  CreditCard,
  DollarSign,
  Download,
  HandCoins,
  Landmark,
  ReceiptText,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'

const ALL_ACCOUNTS_VALUE = 'all'

export const Route = createFileRoute('/_authenticated/$orgId/reports/platform-ledger/')({
  component: PlatformLedgerReportPage,
  validateSearch: (search) => platformLedgerReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (Number(params.orgId) !== Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)) {
      return { isReadOnly: true }
    }

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
    const filters = getReportFilters(search, organization?.timezone)

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      platformLedgerQueries.entries({
        organizationId: params.orgId,
        filters,
      })
    )

    return { isReadOnly: true }
  },
})

export function PlatformLedgerReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const organizationNow = getOrganizationNow(organization?.timezone)
  const requestedFilters = getReportFilters(search, organization?.timezone, {
    normalizeOutlook: false,
    organizationNow,
  })
  const filters = normalizeHistoricalOutlookFilters(requestedFilters, organizationNow)
  const { data: report, isFetching } = usePlatformLedgerEntries(filters)
  const exportMutation = useExportPlatformLedgerEntriesMutation()
  const outlookViewsDisabled = isHistoricalDateRange(requestedFilters, organizationNow)
  const outlookViewDowngraded = requestedFilters.view !== filters.view
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )
  const meta = getIndexPaginationMeta(report, filters.page, filters.page_size)
  const rows = report?.rows ?? []
  const summary = report?.summary ?? {}
  const { updateSearchParams, updateSearchTerm } = useIndexTableSearchParams({
    navigate,
    getSearchParams: (previous) =>
      getPlatformLedgerReportSearchParams(
        getReportFilters(previous, organization?.timezone, { organizationNow })
      ),
    searchTerm: filters.search ?? '',
  })
  const columns = getPlatformLedgerColumns({
    orgId,
    path,
    timezone: report?.filters?.timezone ?? organization?.timezone,
  })

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters,
      })

      downloadExportedReport(
        response,
        `platform-ledger-${filters.view}-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Failed to export platform ledger'))
    }
  }

  return (
    <div className="space-y-4 pb-6">
      <AdminPageHeader
        icon={BookOpenCheck}
        title="Platform Ledger"
        description="Fees OneStem withheld, less fees returned, plus payment processing and Radar costs, counted in each shop's own days."
        className="mb-4 mt-2"
        actions={
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <ReportDateRangeFilterBar
              appliedDateMode={REPORT_DATE_MODE.CUSTOM}
              initialFilters={{
                start_date: filters.start_date,
                end_date: filters.end_date,
                ...datePickerBounds,
              }}
              now={organizationNow}
              onApply={({ start_date, end_date }) =>
                updateSearchParams({
                  start_date,
                  end_date,
                  page: 1,
                })
              }
            />

            <AdminHeaderActionButton
              type="button"
              variant="outline"
              size="lg"
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              <Download className="h-4 w-4" />
              Export Spreadsheet
            </AdminHeaderActionButton>
          </div>
        }
      />

      <div className="@container/ledger-summary">
        <div className="grid grid-cols-1 gap-4 @[32rem]/ledger-summary:grid-cols-2 @[52rem]/ledger-summary:grid-cols-3 @[76rem]/ledger-summary:grid-cols-6">
          <InfoCard
            icon={DollarSign}
            tone="mint"
            title={grossRevenueTitle(filters.view)}
            description={grossRevenueDescription(filters.view)}
            value={formatCurrency(summary.gross_revenue ?? 0)}
          />
          <InfoCard
            icon={ReceiptText}
            tone="periwinkle"
            title="Platform Fees"
            description="Platform fee revenue"
            value={formatCurrency(summary.platform_fee_revenue ?? 0)}
          />
          <InfoCard
            icon={HandCoins}
            tone="amethyst"
            title="POS Fees"
            description="POS fee revenue"
            value={formatCurrency(summary.pos_fee_revenue ?? 0)}
          />
          <InfoCard
            icon={BadgeDollarSign}
            tone="brand"
            title="Merchant Fee Revenue"
            description="v2 Merchant Fees with matched payment processing costs"
            value={
              summary.has_v2_activity
                ? formatCurrency(
                    summary.included_merchant_fee_revenue ?? summary.merchant_fee_revenue
                  )
                : '—'
            }
          />
          <InfoCard
            icon={CreditCard}
            tone="destructive"
            title="Processing Costs"
            description="Card, Link, and Radar costs"
            value={summary.has_v2_activity ? formatCurrency(summary.processor_cost) : '—'}
          />
          <InfoCard
            icon={Banknote}
            tone="mint"
            title="Platform Revenue"
            value={formatCurrency(summary.net_revenue ?? 0)}
            titleEnd={
              <PlatformEarningsDetails
                summary={summary}
                coverage={report?.processing_cost_coverage}
              />
            }
          />
        </div>
      </div>

      {outlookViewDowngraded ? (
        <InfoNotice compact tone="neutral" title="Cash view shown" icon={<Clock3 />}>
          Fees still owed is today's outlook, so it is available when the date range ends today.
        </InfoNotice>
      ) : null}

      <Card>
        <CardHeader>
          <div className="space-y-1.5">
            <CardTitle>Ledger Entries</CardTitle>
            <CardDescription>
              {viewLabel(filters.view)} entries reported from {filters.start_date} to{' '}
              {filters.end_date}.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <IndexDataTable
            data={rows}
            columns={columns}
            sort={filters.sort}
            onSortChange={(sort) =>
              updateSearchParams({
                sort,
                page: 1,
              })
            }
            getRowId={(row) => String(row.id)}
            toolbar={
              <IndexTableToolbar
                primary={
                  <IndexTableSearchBox
                    value={filters.search ?? ''}
                    onCommit={updateSearchTerm}
                    countText={entryCountText(meta.total)}
                    isFetching={isFetching}
                    placeholder="Search ledger..."
                  />
                }
                secondary={
                  <>
                    <PlatformLedgerViewSelect
                      value={filters.view}
                      disableOutlookViews={outlookViewsDisabled}
                      onValueChange={(view) =>
                        updateSearchParams({
                          view,
                          page: 1,
                        })
                      }
                    />
                    <PlatformLedgerAccountSelect
                      value={filters.account ?? ALL_ACCOUNTS_VALUE}
                      onValueChange={(value) =>
                        updateSearchParams({
                          account: value === ALL_ACCOUNTS_VALUE ? undefined : value,
                          page: 1,
                        })
                      }
                    />
                  </>
                }
              />
            }
            emptyState={
              <Typography variant="supporting">
                {filters.search
                  ? 'No ledger entries match your search.'
                  : 'No platform ledger entries match these filters.'}
              </Typography>
            }
            tableClassName="text-sm"
          />

          <IndexTablePagination
            meta={meta}
            pageSize={filters.page_size}
            onPageChange={(page) => updateSearchParams({ page })}
            onPageSizeChange={(pageSize) =>
              updateSearchParams({
                page_size: pageSize,
                page: 1,
              })
            }
          />
        </CardContent>
      </Card>
    </div>
  )
}

type ReportFilterOptions = {
  normalizeOutlook?: boolean
  organizationNow?: Dayjs
}

function getReportFilters(
  search: unknown,
  organizationTimezone: string,
  options: ReportFilterOptions = {}
): PlatformLedgerReportFilters {
  const organizationNow = options.organizationNow ?? getOrganizationNow(organizationTimezone)
  const filters = getPlatformLedgerReportFiltersFromSearch(
    search,
    getDefaultPlatformLedgerReportFilters(organizationNow)
  )

  return options.normalizeOutlook === false
    ? filters
    : normalizeHistoricalOutlookFilters(filters, organizationNow)
}

function normalizeHistoricalOutlookFilters(
  filters: PlatformLedgerReportFilters,
  organizationNow: Dayjs
): PlatformLedgerReportFilters {
  if (!isHistoricalOutlookRange(filters, organizationNow)) {
    return filters
  }

  return {
    ...filters,
    view: PLATFORM_LEDGER_VIEW.CASH,
  }
}

function isHistoricalOutlookRange(filters: PlatformLedgerReportFilters, organizationNow: Dayjs) {
  return (
    filters.view !== PLATFORM_LEDGER_VIEW.CASH && isHistoricalDateRange(filters, organizationNow)
  )
}

function isHistoricalDateRange(filters: PlatformLedgerReportFilters, organizationNow: Dayjs) {
  return filters.end_date < organizationNow.format('YYYY-MM-DD')
}

function getPlatformLedgerColumns({
  orgId,
  path,
  timezone,
}: {
  orgId: number
  path: PathHelpers
  timezone: string
}): ColumnDef<PlatformLedgerEntry>[] {
  return [
    {
      accessorKey: 'reporting_at',
      header: 'Reporting Date',
      cell: ({ row }) =>
        formatRecognizedAt(row.original.reporting_at ?? row.original.recognized_at, timezone),
      meta: {
        kind: 'date',
        minWidth: '7.75rem',
        sortable: {
          asc: PLATFORM_LEDGER_SORT.RECOGNIZED_ASC,
          desc: PLATFORM_LEDGER_SORT.RECOGNIZED_DESC,
          defaultDirection: 'desc',
        },
      },
    },
    {
      accessorKey: 'organization_name',
      header: 'Florist',
      cell: ({ row }) => (
        <LedgerCellLink
          link={path.organizations.show(row.original.organization_id).using({ orgId }).link}
        >
          {row.original.organization_name || `Organization #${row.original.organization_id}`}
        </LedgerCellLink>
      ),
      meta: {
        kind: 'primary',
        minWidth: '12rem',
        sortable: {
          asc: PLATFORM_LEDGER_SORT.FLORIST_ASC,
          desc: PLATFORM_LEDGER_SORT.FLORIST_DESC,
        },
      },
    },
    {
      accessorKey: 'basis',
      header: 'Basis',
      cell: ({ row }) => <BasisBadge basis={row.original.basis} label={row.original.basis_label} />,
      meta: {
        kind: 'badge',
        align: 'center',
        link: false,
      },
    },
    {
      accessorKey: 'account',
      header: 'Account',
      cell: ({ row }) => row.original.account_label || accountLabel(row.original.account),
      meta: {
        kind: 'text',
        minWidth: '12rem',
        sortable: {
          asc: PLATFORM_LEDGER_SORT.ACCOUNT_ASC,
          desc: PLATFORM_LEDGER_SORT.ACCOUNT_DESC,
        },
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => <AmountCell amount={row.original.amount} account={row.original.account} />,
      meta: {
        kind: 'money',
        align: 'end',
        sortable: {
          asc: PLATFORM_LEDGER_SORT.AMOUNT_ASC,
          desc: PLATFORM_LEDGER_SORT.AMOUNT_DESC,
          defaultDirection: 'desc',
        },
      },
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => <OrderSourceBadge source={row.original.source} size="sm" />,
      meta: {
        kind: 'badge',
        align: 'center',
        minWidth: '8rem',
      },
    },
    {
      accessorKey: 'sales_order_id',
      header: 'Order',
      cell: ({ row }) =>
        row.original.sales_order_id ? (
          <LedgerCellLink
            link={
              path.salesOrders
                .show(row.original.sales_order_id)
                .using({ orgId: row.original.organization_id }).link
            }
          >
            {formatIdentifier(row.original.sales_order_id)}
          </LedgerCellLink>
        ) : (
          <MutedIdentifier />
        ),
      meta: {
        kind: 'id',
        align: 'center',
      },
    },
    {
      accessorKey: 'payment_id',
      header: 'Payment',
      cell: ({ row }) => formatIdentifierOrMuted(row.original.payment_id),
      meta: {
        kind: 'id',
        align: 'center',
      },
    },
    {
      accessorKey: 'refund_request_id',
      header: 'Refund',
      cell: ({ row }) => formatIdentifierOrMuted(row.original.refund_request_id),
      meta: {
        kind: 'id',
        align: 'center',
      },
    },
    {
      id: 'processing_details',
      header: 'Processing Details',
      cell: ({ row }) => {
        const { metadata } = row.original
        const reportRunId = metadataText(metadata, 'report_run_id')

        if (!reportRunId) {
          return <MutedIdentifier />
        }

        const details: [string, ReactNode][] = [
          [
            metadataText(metadata, 'incurred_by_type') === 'payment_intent'
              ? 'PaymentIntent'
              : 'Charge',
            metadataText(metadata, 'incurred_by'),
          ],
          ['Feature', metadataText(metadata, 'feature_name')],
          ['Balance transaction', metadataText(metadata, 'balance_transaction_id')],
          ['Ledger date', formatRecognizedAt(row.original.recognized_at, timezone)],
          ['Import report', reportRunId],
          ['Attribution', metadataText(metadata, 'attribution_status')],
        ]

        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm">
                View cost details
              </Button>
            </PopoverTrigger>
            <PopoverContent size="wide">
              <dl className="space-y-2 text-sm">
                {details.map(([label, value]) => (
                  <div key={label}>
                    <Typography as="dt" variant="caption" tone="muted">
                      {label}
                    </Typography>
                    <dd className="break-words">{value || '—'}</dd>
                  </div>
                ))}
              </dl>
            </PopoverContent>
          </Popover>
        )
      },
      meta: { kind: 'text', link: false },
    },
  ]
}

function LedgerCellLink({ link, children }: { link: PathLink; children: ReactNode }) {
  return (
    <Link
      {...link}
      className="font-medium text-foreground underline-offset-4 hover:underline"
      draggable={false}
    >
      {children}
    </Link>
  )
}

function PlatformLedgerViewSelect({
  value,
  disableOutlookViews = false,
  onValueChange,
}: {
  value: PlatformLedgerView
  disableOutlookViews?: boolean
  onValueChange: (view: string) => void
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Filter ledger view" className="w-fit min-w-[180px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PLATFORM_LEDGER_VIEW_OPTIONS.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            disabled={disableOutlookViews && option.value !== PLATFORM_LEDGER_VIEW.CASH}
          >
            <BookOpenCheck className="size-4" />
            {option.label}
          </SelectItem>
        ))}
        {disableOutlookViews ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            Fees still owed needs a range ending today
          </div>
        ) : null}
      </SelectContent>
    </Select>
  )
}

function PlatformLedgerAccountSelect({
  value,
  onValueChange,
}: {
  value: string
  onValueChange: (value: string) => void
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Filter ledger account" className="w-fit min-w-[170px]">
        <SelectValue placeholder="All accounts" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_ACCOUNTS_VALUE}>
          <Landmark className="size-4" />
          All accounts
        </SelectItem>
        {PLATFORM_LEDGER_ACCOUNT_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function BasisBadge({ basis, label }: { basis: PlatformLedgerBasis; label: string }) {
  return (
    <Badge variant={basis === PLATFORM_LEDGER_BASIS.OWED ? 'outline' : 'secondary'}>
      {label || basisLabel(basis)}
    </Badge>
  )
}

function AmountCell({ amount, account }: { amount: string; account: PlatformLedgerAccount }) {
  const numericAmount = parseCurrency(amount)

  return (
    <span
      className={cn(
        'font-medium tabular-nums',
        account === PLATFORM_LEDGER_ACCOUNT.PROCESSOR_COST &&
          'text-status-destructive-foreground dark:text-status-destructive-foreground',
        numericAmount < 0 && 'text-muted-foreground'
      )}
    >
      {formatCurrency(numericAmount)}
    </span>
  )
}

function MutedIdentifier() {
  return <Typography variant="supporting">-</Typography>
}

/** A processing cost's fee report detail; fee records have no metadata (`[]`). */
function metadataText(metadata: PlatformLedgerEntryMetadata, key: string): string | undefined {
  const value = Array.isArray(metadata) ? undefined : metadata?.[key]

  return typeof value === 'string' ? value : undefined
}

function formatIdentifier(value: number | null) {
  return value ? `#${value}` : '-'
}

function formatIdentifierOrMuted(value: number | null) {
  return value ? formatIdentifier(value) : <MutedIdentifier />
}

function formatRecognizedAt(value: string, timezone: string) {
  if (!value) {
    return <MutedIdentifier />
  }

  const recognizedAt = dayjs(value).tz(timezone || dayjs.tz.guess())

  return (
    <span className="inline-flex flex-col whitespace-nowrap leading-tight tabular-nums">
      <span>{recognizedAt.format('MMM D, YYYY')}</span>
      <span className="text-muted-foreground">{recognizedAt.format('h:mm A')}</span>
    </span>
  )
}

function basisLabel(basis: PlatformLedgerBasis) {
  return basis === PLATFORM_LEDGER_BASIS.OWED ? 'Still owed' : 'Cash'
}

function accountLabel(account: PlatformLedgerAccount) {
  return (
    PLATFORM_LEDGER_ACCOUNT_OPTIONS.find((option) => option.value === account)?.label ??
    unslugify(account)
  )
}

function entryCountText(total: number) {
  return `${total} entr${total === 1 ? 'y' : 'ies'}`
}

function viewLabel(view: PlatformLedgerView) {
  return PLATFORM_LEDGER_VIEW_OPTIONS.find((option) => option.value === view)?.label ?? 'Cash'
}

function grossRevenueTitle(view: PlatformLedgerView) {
  return view === PLATFORM_LEDGER_VIEW.FEES_OWED ? 'Fees Still Owed' : 'Gross Revenue'
}

function grossRevenueDescription(view: PlatformLedgerView) {
  return view === PLATFORM_LEDGER_VIEW.FEES_OWED
    ? 'Website fees orders still owe, never counted as revenue'
    : 'Platform, POS, and Merchant Fee revenue'
}
